import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { news, newsCategories } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import NewsCard from "@/components/feature/NewsCard";

const PAGE_SIZE = 6;

export default function NewsListPage() {
  const { category = "" } = useParams();
  const info = newsCategories.find((c) => c.key === category);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [category]);

  usePageMeta({
    title: info ? `${info.name} - 影视资讯 - 黄色在线` : "影视资讯 - 黄色在线",
    description: info
      ? `${info.name}：${info.desc} 每日更新最新影视资讯内容摘要与深度解读。`
      : "黄色在线影视资讯栏目。",
    keywords: `黄色在线,影视资讯,${info ? info.name : "影视新闻"},影视动态`,
  });

  const list = useMemo(
    () =>
      news
        .filter((n) => n.category === category)
        .sort((a, b) => b.publishDate.localeCompare(a.publishDate)),
    [category]
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageList = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb light items={[{ label: "影视资讯", to: "/news/industry" }, { label: info ? info.name : "资讯" }]} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
          {info ? info.name : "影视资讯"}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300">
          {info ? info.desc : "最新影视行业动态与深度内容。"}
        </p>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-wrap gap-2">
          {newsCategories.map((c) => (
            <Link
              key={c.key}
              to={`/news/${c.key}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                c.key === category
                  ? "bg-accent-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-semibold text-accent-600">{list.length}</span> 篇资讯
          </p>
        </div>

        {pageList.length > 0 ? (
          <div className="mt-4 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 p-2 md:p-4">
            {pageList.map((item) => (
              <div key={item.id} className="py-2">
                <NewsCard item={item} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
            <i className="ri-newspaper-line text-4xl text-foreground-300" aria-hidden="true" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">暂无相关资讯</h2>
            <p className="text-sm text-foreground-500">该分类下暂时还没有内容，去看看其他栏目吧。</p>
            <Link
              to="/news/industry"
              className="mt-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              查看行业动态
            </Link>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="mt-8">
            <Pagination
              current={page}
              total={totalPages}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}